"use client";
import React from 'react';
import { motion } from 'framer-motion';
import { useContactModal } from '../../../../contexts/ContactModalContext';

const cards = [
    {
        col: 'md:col-span-2',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="7" stroke="#ffffff" strokeWidth="1.6" />
                <path d="M10 7v3.5l2.2 2.2" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        ),
        text: 'Most businesses looking for an SEO company in Bangalore have experienced the same frustration.',
        highlight: false,
    },
    {
        col: 'md:col-span-3',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 15l4-5 3 2 4-6" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3 15h14" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
        ),
        text: 'Traffic increases. Rankings improve. But revenue barely moves.',
        highlight: false,
    },
    {
        col: 'md:col-span-5',
        icon: null,
        text: 'Because rankings matter. Revenue matters more.',
        highlight: true,
    },
    {
        col: 'md:col-span-5',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="8" cy="8" r="5" stroke="#ffffff" strokeWidth="1.6" />
                <path d="M13 13l4 4" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M6 8h4" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
        ),
        text: "That's because most SEO agencies optimise for visibility alone — we optimise for what happens after the click. At Lead Workz, SEO begins with understanding search intent and consumer behaviour, uncovering what your audience is searching for and why, then building a system designed to attract qualified traffic that converts.",
        highlight: false,
    },
];

const container = {
    hidden: {},
    show: {
        transition: { staggerChildren: 0.12 },
    },
};

const item = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] } },
};

export default function SEOHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative bg-white pt-0 pb-0 overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-40 pb-24 overflow-hidden"
                style={{
                    background: '#ffffff',
                    borderBottomLeftRadius: '80px',
                    borderBottomRightRadius: '80px',
                }}
            >
                {/* Subtle noise texture */}
                <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
                }} />

                {/* Ambient glows */}
                <div className="absolute pointer-events-none" style={{
                    top: '-10%', left: '-8%', width: 460, height: 460,
                    background: 'radial-gradient(circle, rgba(55,48,163,0.10) 0%, transparent 70%)',
                    borderRadius: '50%',
                }} />
                <div className="absolute pointer-events-none" style={{
                    bottom: '-15%', right: '-8%', width: 520, height: 520,
                    background: 'radial-gradient(circle, rgba(206,255,26,0.12) 0%, transparent 70%)',
                    borderRadius: '50%',
                }} />
                <div className="absolute pointer-events-none" style={{
                    top: '30%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 300,
                    background: 'radial-gradient(ellipse, rgba(55,48,163,0.05) 0%, transparent 70%)',
                }} />

                <div className="max-w-[1200px] mx-auto flex flex-col items-center gap-12">

                    {/* Eyebrow */}
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-sm md:text-base font-semibold tracking-[0.16em] uppercase text-center"
                        style={{ color: '#3730a3', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        SEO Company In Bangalore
                    </motion.p>

                    {/* H1 */}
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight text-center"
                        style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            color: '#080400',
                        }}
                    >
                        SEO That Drives Revenue,<br className="hidden md:block" /> Not Just Rankings
                    </motion.h1>

                    {/* Creative card grid */}
                    <motion.div
                        variants={container}
                        initial="hidden"
                        animate="show"
                        className="w-full grid grid-cols-1 md:grid-cols-5 gap-3"
                    >
                        {cards.map((card, i) => (
                            <motion.div
                                key={i}
                                variants={item}
                                whileHover={{ y: -5 }}
                                transition={{ duration: 0.25 }}
                                className={card.col}
                            >
                                {card.highlight ? (
                                    <div
                                        className="relative w-full rounded-2xl px-7 py-8 flex items-center justify-center overflow-hidden"
                                        style={{
                                            background: 'linear-gradient(135deg, #3730a3 0%, #5f1fd6 100%)',
                                            boxShadow: '0 20px 45px -10px rgba(55,48,163,0.45)',
                                        }}
                                    >
                                        {/* decorative quote mark */}
                                        <span className="absolute select-none pointer-events-none" style={{
                                            top: -18, left: 14, fontSize: 120, fontWeight: 900,
                                            color: 'rgba(255,255,255,0.08)',
                                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                            lineHeight: 1,
                                        }}>&ldquo;</span>
                                        <p
                                            className="relative z-10 text-center font-bold text-lg md:text-2xl leading-snug"
                                            style={{
                                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                                color: '#ffffff',
                                                letterSpacing: '0.02em',
                                            }}
                                        >
                                            {card.text}
                                        </p>
                                    </div>
                                ) : (
                                    <div
                                        className="h-full rounded-2xl px-6 py-6 flex flex-col gap-4 transition-shadow duration-300"
                                        style={{
                                            background: i === 3
                                                ? 'linear-gradient(160deg, rgba(206,255,26,0.10) 0%, rgba(8,4,0,0.02) 60%)'
                                                : 'linear-gradient(160deg, rgba(55,48,163,0.05) 0%, rgba(8,4,0,0.02) 60%)',
                                            border: '1px solid rgba(8,4,0,0.07)',
                                            boxShadow: '0 8px 24px -12px rgba(8,4,0,0.15)',
                                        }}
                                    >
                                        {card.icon && (
                                            <div
                                                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                                                style={{
                                                    background: 'linear-gradient(135deg, #3730a3 0%, #6d2fe0 100%)',
                                                    boxShadow: '0 6px 16px -4px rgba(55,48,163,0.5)',
                                                }}
                                            >
                                                {card.icon}
                                            </div>
                                        )}
                                        <p
                                            className="text-sm md:text-base leading-relaxed"
                                            style={{
                                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                                color: 'rgba(8,4,0,0.65)',
                                                letterSpacing: '0.03em',
                                            }}
                                        >
                                            {card.text}
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                    >
                        <button
                            onClick={openModal}
                            className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                            style={{
                                background: '#3730a3',
                                color: '#ffffff',
                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                letterSpacing: '0.08em',
                            }}
                        >
                            Get Your Free SEO Audit
                        </button>
                    </motion.div>

                </div>
            </div>
        </section>
    );
}
